import * as React from "react";
import {
  useLoaderData,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";
import { LoaderData } from "../MyAccount";
import { AccountHolder, useAccount } from "../../../Account";
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  SelectChangeEvent,
  TablePagination,
  Toolbar,
  Typography,
} from "@mui/material";
import { useLanguage } from "../../../Language";
import PrintIcon from "@mui/icons-material/Print";
import {
  cancelHold,
  getAllHoldsSortable,
  SuspendHoldInfo,
  unsuspendHold,
} from "../../../services/Holds";
import { HostSystemItemSortType } from "../../../../dto/HostSystemItemSortType";
import { TLCEventManager } from "../../../../events/EventManager";
import { PrintAccountDataEvent } from "@tlc/ls2pac/app/managers/PrintAccountDataManager";
import { LS2PAC_CONSTANTS } from "src/main/resources/js/main/constants";
import { TLCEvents } from "../../../../events/events";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { EResourcesAvailabilityClientManager } from "@tlc/ls2pac/app/ClientManagers/EResourcesAvailabilityClientManager";
import { AvailabilityFactory } from "src/main/resources/javascript/tlc/availability/AvailabilityFactory";
import { ResourceUtils } from "@tlc/ls2pac/app/utils/resourceUtils";
import { useGlobalConfig } from "../../../GlobalConfig";
import { UpdateDigitalLoansButton } from "../UpdateDigitalLoansButton";
import { HoldsSortControl } from "../HostSystemItemSortControl";
import { HostSystemItemView } from "@tlc/ls2pac/dto/HostSystemItemView";
import { useEffect, useMemo, useState } from "react";
import { CancelHoldButton } from "../CancelHoldButton";
import { SuspendHoldsDialog } from "./SuspendHoldsDialog";
import { holdsLoader } from "./holdsLoader";
import { HoldsRowItem } from "./HoldsRowItem";
import { LSResultItemSkeleton } from "../../search/ls/LSResultItem/LSResultItemSkeleton";

function getKey(bibliographicId: string, holdOrDesireNumber: string | number) {
  // holdNumber/desireNumber is always 1 for eBooks
  // bibliographicId may be duplicated, (multiple item level holds on the same title, etc.)
  // key is constructed using properties depended on by cancel, suspend, unsuspend API calls
  return `${bibliographicId}_${holdOrDesireNumber}`;
}

function getKeyForHold({
  bibliographicId,
  holdNumber,
}: Pick<HostSystemItemView, "bibliographicId" | "holdNumber">): string {
  return getKey(bibliographicId, holdNumber);
}

function getKeyForSuspendHoldInfo({
  bibliographicId,
  desireNumber,
}: Pick<SuspendHoldInfo, "bibliographicId" | "desireNumber">) {
  return getKey(bibliographicId, desireNumber);
}

// This function was taken almost 1:1 from HoldsView
// It has several typescript errors due to formatInfo being typed
// as EResourceFormat[] and not EResourceFormat
// I currently have no way to test this and confirm there is a problem
// using ts-ignore for now to compile
function onCheckoutReservedEBook(hold: HostSystemItemView[]) {
  // @ts-ignore
  let formatInfo;
  let currentHold;

  const storeFormatInfo = (hold: HostSystemItemView[]) => {
    formatInfo = hold[0].formatInfo;

    if (formatInfo) {
      // @ts-ignore
      formatInfo.formatName = formatInfo.formatId;

      if (
        // @ts-ignore
        formatInfo.formatToken &&
        // @ts-ignore
        formatInfo.formatToken != "<InvalidEResourceFormat>"
      ) {
        // @ts-ignore
        formatInfo.formatName = ResourceUtils.getMessageByMessageTokenString(
          // @ts-ignore
          formatInfo.formatToken
        );
      }
    }
  };
  // Storing format info from history table
  storeFormatInfo(hold);

  EResourcesAvailabilityClientManager.getDownloadableItemAvailability({
    downloadableResources: hold,
    resources: hold,
    callCanAbort: false,
    // @ts-ignore
    callback: function (newHold) {
      currentHold = newHold[0];
      currentHold.resource.eAvailabilities = currentHold.eAvailabilities;

      //TODO: This is assuming one availability source -- need to modify to handle more than one on one record
      if (currentHold.resource.eAvailabilities[0].formatInfo.length == 0) {
        // @ts-ignore
        currentHold.resource.eAvailabilities[0].formatInfo = formatInfo;
      }

      AvailabilityFactory.getDisplayObject(currentHold.resource);

      TLCEvents.trigger(LS2PAC_CONSTANTS.Events.onAttemptEResourceCheckout, {
        resource: currentHold.resource,
        referrer: "holds",
      });
    },
  });
}

interface GetAllHoldsEvent {
  total: number;
}

export const AccountHoldsPage: React.FC = () => {
  const revalidator = useRevalidator();
  const { translate } = useLanguage();
  const { accountSummary, user, patron } = useAccount();
  const [searchParams, setSearchParams] = useSearchParams();
  const { allowBorrowersToCancelArrivedHolds } = useGlobalConfig();
  const { sort, page, pageSize, refreshToken } = useLoaderData() as LoaderData<
    typeof holdsLoader
  >;

  const [loading, setLoading] = useState<boolean>();
  const [holds, setHolds] = useState<HostSystemItemView[]>([]);
  useEffect(() => {
    setLoading(true);
    getAllHoldsSortable(page, pageSize, sort, true)
      .then(({ holds }) => {
        setHolds(holds);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [page, pageSize, sort, refreshToken]);

  const total = useMemo<number>(
    () => accountSummary.arrivedHolds + accountSummary.pendingHolds,
    [accountSummary]
  );

  const [holdsToSuspend, setHoldsToSuspend] = React.useState<
    HostSystemItemView[]
  >([]);

  React.useEffect(() => {
    // Until we refactor PrintAccountDataManager we need this
    const getAllHolds = async (event: GetAllHoldsEvent) => {
      try {
        const { holds } = await getAllHoldsSortable(
          0,
          event.total,
          sort,
          false
        );
        TLCEventManager.fireEvent(
          LS2PAC_CONSTANTS.Events.onGetAllHoldsResponse,
          { success: true, data: holds }
        );
      } catch (e) {
        console.error(e);
        TLCEventManager.fireEvent(
          LS2PAC_CONSTANTS.Events.onGetAllHoldsResponse,
          { success: false }
        );
      }
    };
    const getAllHoldsListenerId = TLCEventManager.addListener(
      LS2PAC_CONSTANTS.Events.onGetAllHolds,
      getAllHolds
    );

    const eResourceCheckoutResponseListenerId = TLCEvents.on(
      LS2PAC_CONSTANTS.Events.onEResourceCheckoutResponse,
      (event: { success: boolean }) => {
        if (event.success) {
          refreshPage();
          AccountHolder.fetchAccountSummaryAndPatron(true);
        }
      }
    );

    return () => {
      TLCEvents.off(
        LS2PAC_CONSTANTS.Events.onEResourceCheckoutResponse,
        eResourceCheckoutResponseListenerId
      );
      TLCEventManager.removeListener(
        LS2PAC_CONSTANTS.Events.onGetAllHolds,
        getAllHoldsListenerId
      );
    };
  }, []);

  useEffect(() => {
    setSelected([]);
  }, [page, pageSize, sort]);

  const onClickSuspendHolds = () => {
    const suspendableHolds = selected.filter(
      (hold) => hold.status !== "T" && hold.status !== "AR"
    );
    setHoldsToSuspend(suspendableHolds);
  };

  const onClickCancelHolds = async () => {
    const cancellableHolds = selected.filter(
      (hold) =>
        hold.status !== "T" &&
        (hold.status !== "AR" || allowBorrowersToCancelArrivedHolds)
    );
    const { cancelHoldInfos } = await cancelHold(cancellableHolds);
    const successKeys = cancelHoldInfos
      .filter((info) => info.success === true)
      .map((info) => getKeyForSuspendHoldInfo(info));
    const successCount = successKeys.length;
    const failureCount = cancelHoldInfos.filter(
      (info) => info.success === false
    ).length;
    // TODO cancelHoldFailureMessage
    if (successCount > 0) {
      SuccessMessageControl.show(
        `${successCount} ${translate("canceledMessage")}`
      );
      setSelected(
        selected.filter(
          (hold) => successKeys.indexOf(getKeyForHold(hold)) === -1
        )
      );
      await AccountHolder.fetchAccountSummaryAndPatron(true); // force update of counts
      refreshPage();
    }
  };

  const onClickReactivateHolds = async () => {
    const { suspendHoldInfos } = await unsuspendHold(selected);

    const successKeys = suspendHoldInfos
      .filter((info) => info.success === true)
      .map((info) => getKeyForSuspendHoldInfo(info));
    const successCount = successKeys.length;
    setSelected(
      selected.filter((hold) => successKeys.indexOf(getKeyForHold(hold)) === -1)
    );
    const failureCount = suspendHoldInfos.filter(
      (info) => info.success === false
    ).length;
    // TODO unsuspendHoldFailureMessage
    if (successCount > 0) {
      SuccessMessageControl.show(
        `${successCount} ${translate("unsuspendMessage")}`
      );
      refreshPage();
    }
  };

  const onClickPrintHolds = () => {
    TLCEventManager.fireEvent(PrintAccountDataEvent, {
      sections: [LS2PAC_CONSTANTS.MyAccountPrintTypes.holds],
    });
  };

  const [selected, setSelected] = React.useState<HostSystemItemView[]>([]);
  const selectedRows = React.useMemo<string[]>(
    () => selected.map((row) => getKeyForHold(row)),
    [selected]
  );

  const handleSelectAllClick = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setSelected(holds);
    } else setSelected([]);
  };

  const isSelected = (row: HostSystemItemView) => {
    const key = getKeyForHold(row);
    return selectedRows.indexOf(key) !== -1;
  };

  const handleClick = (
    event: React.MouseEvent<unknown>,
    hold: HostSystemItemView
  ) => {
    const key = getKeyForHold(hold);
    const selectedIndex = selectedRows.indexOf(key);

    if (selectedIndex === -1) {
      setSelected([...selected, hold]);
    } else {
      setSelected(selected.filter((tc) => getKeyForHold(tc) !== key));
    }
  };

  const refreshPage = async () => {
    revalidator.revalidate();
  };

  const handleSortChange = async function (event: SelectChangeEvent) {
    const newSort = event.target.value as HostSystemItemSortType;
    searchParams.set("page", "0");
    searchParams.set("sort", newSort);
    setSearchParams(searchParams);
  };

  const handleChangePage = async (event: unknown, newPage: number) => {
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  const handleChangeRowsPerPage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    searchParams.set("page", "0");
    searchParams.set("size", event.target.value);
    setSearchParams(searchParams);
  };

  const reactivateDisabled = React.useMemo(() => {
    if (loading) {
      return true;
    }
    if (selected.length === 0) {
      return true;
    }

    // disable if NONE of the selected holds have suspend dates
    return !selected.some((value) => {
      return !!value.suspendUntilDate;
    });
  }, [selected, loading]);

  const suspendDisabled = React.useMemo(() => {
    if (loading) {
      return true;
    }
    if (selected.length === 0) {
      return true;
    }

    // disable if every hold is arrived or in transit
    return selected.every((hold) => {
      return hold.status == "AR" || hold.status == "T";
    });
  }, [selected, loading]);

  const cancelDisabled = React.useMemo(() => {
    if (loading) {
      return true;
    }
    if (selected.length === 0) {
      return true;
    }

    // Disable if every hold is in transit, or arrived and allowBorrowersToCancelArrivedHolds is also disabled
    return selected.every((hold) => {
      return (
        (hold.status == "AR" && !allowBorrowersToCancelArrivedHolds) ||
        hold.status == "T"
      );
    });
  }, [selected, loading]);

  const updateLoansButton = React.useMemo(() => {
    return (
      <UpdateDigitalLoansButton
        disabled={loading}
        patronId={patron.patronId}
        onUpdate={() => {
          AccountHolder.fetchAccountData(true);
        }}
        lastUpdated={accountSummary.digitalInformationLastUpdated}
      ></UpdateDigitalLoansButton>
    );
  }, [patron.patronId, accountSummary.digitalInformationLastUpdated, loading]);

  return (
    <Box>
      {total === 0 && (
        <Alert severity="info" action={updateLoansButton}>
          {translate("noHoldsMessage")} {translate("request")}.
        </Alert>
      )}
      {total > 0 && (
        <>
          <Toolbar
            sx={{
              flexWrap: "wrap",
              gap: 1,
              pl: { sm: 2 },
              pr: { xs: 1, sm: 1 },
              ...(selected.length > 0 && {
                bgcolor: (theme) =>
                  alpha(
                    theme.palette.primary.main,
                    theme.palette.action.activatedOpacity
                  ),
              }),
            }}
          >
            <Checkbox
              color="primary"
              indeterminate={
                selected.length > 0 && selected.length < holds.length
              }
              disabled={loading}
              checked={selected.length > 0 && selected.length === holds.length}
              onChange={handleSelectAllClick}
              slotProps={{
                input: {
                  "aria-label": translate("selectAll"),
                },
              }}
            />
            {selected.length === 0 && (
              <>
                <Typography
                  sx={{ flex: "1 1", textWrap: "nowrap" }}
                  color="inherit"
                  variant="h6"
                >
                  {translate("requests")}: {patron.pendingHolds}{" "}
                  <Box
                    component={"span"}
                    sx={{
                      color:
                        patron.arrivedHolds > 0 ? "success.main" : "inherit",
                    }}
                  >
                    {translate("arrived")}: {patron.arrivedHolds}
                  </Box>
                </Typography>
                {updateLoansButton}
                <Button
                  onClick={onClickPrintHolds}
                  disabled={total === 0 || loading}
                  startIcon={<PrintIcon />}
                >
                  {translate("print")} {translate("requests")}
                </Button>
                <HoldsSortControl
                  disabled={loading}
                  value={sort}
                  onChange={handleSortChange}
                ></HoldsSortControl>
              </>
            )}
            {selected.length > 0 && (
              <>
                <Typography
                  sx={{ flex: "1 1", textWrap: "nowrap" }}
                  color="inherit"
                  variant="subtitle1"
                  component="div"
                >
                  {selected.length} {translate("selected")}
                </Typography>
                <Button
                  disabled={reactivateDisabled}
                  onClick={onClickReactivateHolds}
                >
                  {translate("reactivate")}
                </Button>
                <Button
                  disabled={suspendDisabled}
                  onClick={onClickSuspendHolds}
                >
                  {translate("suspend")}
                </Button>
                <CancelHoldButton
                  disabled={cancelDisabled}
                  onClick={onClickCancelHolds}
                />
              </>
            )}
          </Toolbar>
          <div className="resultItems">
            <ul>
              {loading ? (
                <>
                  {Array.from({ length: pageSize }).map((_, index) => (
                    <div key={index}>
                      <LSResultItemSkeleton />
                    </div>
                  ))}
                </>
              ) : (
                holds.map((row, index) => {
                  const isItemSelected = isSelected(row);
                  const key = getKeyForHold(row);

                  return (
                    <li key={key} className="item mainResultItem clearfix">
                      <Box>
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          onClick={(event) => {
                            handleClick(event, row);
                          }}
                          slotProps={{
                            input: {
                              "aria-label": translate("select"),
                            },
                          }}
                        />
                      </Box>

                      <HoldsRowItem
                        id={key}
                        hold={row}
                        onClickCheckout={() => {
                          onCheckoutReservedEBook([row]);
                        }}
                        onClickCancel={async () => {
                          const { cancelHoldInfos } = await cancelHold([row]);
                          if (cancelHoldInfos[0].success) {
                            SuccessMessageControl.show(
                              `1 ${translate("canceledMessage")}`
                            );
                            // TODO deterimine page
                            refreshPage();
                            await AccountHolder.fetchAccountSummaryAndPatron(
                              true
                            ); // force update of counts
                          } else {
                            // TODO cancelHoldFailureMessage
                          }
                        }}
                        onClickReactivate={async () => {
                          const { suspendHoldInfos } = await unsuspendHold([
                            row,
                          ]);
                          if (suspendHoldInfos[0].success) {
                            SuccessMessageControl.show(
                              `1 ${translate("unsuspendMessage")}`
                            );
                            refreshPage();
                          } else {
                            // TODO unsuspendHoldFailureMessage
                          }
                        }}
                        onClickSuspend={() => setHoldsToSuspend([row])}
                      ></HoldsRowItem>
                    </li>
                  );
                })
              )}
            </ul>
          </div>
          <TablePagination
            disabled={loading}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      <SuspendHoldsDialog
        holds={holdsToSuspend}
        open={holdsToSuspend.length > 0}
        onClose={() => setHoldsToSuspend([])}
        onSuspend={(suspended) => {
          const suspendedKeys = suspended.map((hold) =>
            getKeyForSuspendHoldInfo(hold)
          );
          setSelected(
            selected.filter(
              (hold) => suspendedKeys.indexOf(getKeyForHold(hold)) === -1
            )
          );
          refreshPage();
        }}
      ></SuspendHoldsDialog>
    </Box>
  );
};
